import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, User, Phone, Building2, Smartphone, KeyRound } from "lucide-react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import Logo from "../components/Logo";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getFirebaseAuth } from "../lib/firebase";
import { toast } from "sonner";

function Field({ icon: Icon, testId, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input data-testid={testId} {...props}
        className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15" />
    </div>
  );
}

const STAFF_ROLES = ["admin", "super_admin", "operations", "support", "finance", "warehouse_manager"];

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accountNotFound, setAccountNotFound] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company: "" });
  const [otpStage, setOtpStage] = useState("idle"); // idle | sent
  const [otpForm, setOtpForm] = useState({ phone: "", code: "" });
  const [otpBusy, setOtpBusy] = useState(false);
  const confirmationRef = useRef(null);

  const afterAuth = async (data, welcome) => {
    if (STAFF_ROLES.includes(data.role)) {
      await logout().catch(() => {});
      setUser(null);
      setError("Staff accounts sign in at shipany.in/ai-admin");
      return;
    }
    if (data.role === "franchise") {
      await logout().catch(() => {});
      setUser(null);
      setError("Franchise partners sign in at shipany.in/franchise-login");
      return;
    }
    if (welcome) toast.success(welcome);
    setUser(data);
    navigate(location.state?.from || "/app", { replace: true });
  };

  const handleAuthError = (err, fallback) => {
    if (err?.response?.status === 404) { setAccountNotFound(fmtErr(err)); setError(""); }
    else setError(fallback || fmtErr(err));
  };

  const onGoogleSuccess = async (credResponse) => {
    setError("");
    setAccountNotFound("");
    try {
      const { data } = await api.post("/auth/google/verify", { credential: credResponse.credential });
      await afterAuth(data, `Welcome, ${data.name}!`);
    } catch (err) {
      handleAuthError(err);
    }
  };

  const sendOtp = async () => {
    if (!/^[6-9]\d{9}$/.test(otpForm.phone)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setOtpBusy(true);
    setError("");
    setAccountNotFound("");
    try {
      const { data: chk } = await api.post("/auth/check-phone", { phone: otpForm.phone });
      if (!chk.registered) {
        setAccountNotFound("This mobile number is not registered. Please create a new account to continue.");
        return;
      }
      const auth = getFirebaseAuth();
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch { /* ignore */ }
      }
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
      confirmationRef.current = await signInWithPhoneNumber(auth, `+91${otpForm.phone}`, window.recaptchaVerifier);
      setOtpStage("sent");
      toast.success(`OTP sent to +91 ${otpForm.phone}`);
    } catch (err) {
      if (err?.code === "auth/operation-not-allowed") {
        setError("SMS is not enabled for India (+91) in Firebase Console. Please enable 'Phone' provider and add 'India' to SMS Region Policy in Firebase Console.");
      } else if (err?.code === "auth/too-many-requests") {
        setError("Too many attempts. Try again in a few minutes.");
      } else if (err?.code === "auth/captcha-check-failed") {
        setError("reCAPTCHA check failed. Please refresh the page and try again.");
      } else {
        setError(err?.message || "Could not send OTP");
      }
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otpForm.code)) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setOtpBusy(true);
    setError("");
    setAccountNotFound("");
    try {
      const cred = await confirmationRef.current.confirm(otpForm.code);
      const idToken = await cred.user.getIdToken();
      const { data } = await api.post("/auth/firebase", { id_token: idToken });
      await afterAuth(data, "Signed in with mobile OTP");
    } catch (err) {
      handleAuthError(err, err?.code === "auth/invalid-verification-code" ? "Incorrect OTP. Please try again." : undefined);
    } finally {
      setOtpBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setAccountNotFound("");
    try {
      const { data } = await api.post(isLogin ? "/auth/login" : "/auth/register", form);
      if (isLogin && data.must_change_password) {
        setUser(data);
        toast.info("Temporary password accepted — set your new password to continue");
        navigate("/set-password", { replace: true });
        return;
      }
      await afterAuth(data, isLogin ? `Welcome back, ${data.name}!` : "Account created — welcome aboard!");
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <div className="flex min-h-screen">
        <div className="hero-grid hidden w-1/2 flex-col justify-between bg-shipany-navy p-12 lg:flex">
          <Logo dark />
          <div>
            <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white">
              Ship Anywhere.<br /><span className="text-saffron-green">Ship Smarter.</span>
            </h1>
            <p className="mt-4 max-w-md text-slate-300">India's Smart Multi-Courier Shipping Platform.</p>
            <div className="mt-8 flex gap-6">
              {[["27,000+", "Pincodes"], ["10+", "Couriers"], ["4", "Providers"]].map(([v, l]) => (
                <div key={l}><p className="mono text-xl font-bold text-shipany-cyan">{v}</p><p className="text-xs uppercase tracking-widest text-slate-400">{l}</p></div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} SHIPANY · shipany.in</p>
        </div>

        <div className="flex flex-1 items-center justify-center bg-shipany-mist px-5 py-12">
          <div className="w-full max-w-md">
            <div className="mb-8 lg:hidden"><Logo /></div>
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60" data-testid="auth-card">
              <h2 className="font-display text-2xl font-bold text-shipany-navy">{isLogin ? "Welcome back" : "Create your account"}</h2>
              <p className="mt-1 text-sm text-slate-500">{isLogin ? "Sign in to your SHIPANY dashboard." : "Start shipping in under five minutes."}</p>

              <div className="mt-6 flex justify-center" data-testid="google-login-btn">
                <GoogleLogin onSuccess={onGoogleSuccess} onError={() => setError("Google sign-in failed. Please try again.")}
                  width="352" text={isLogin ? "signin_with" : "signup_with"} shape="rectangular" />
              </div>

              {accountNotFound && (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center" data-testid="account-not-found">
                  <p className="text-sm font-medium text-amber-800">{accountNotFound}</p>
                  <Link to="/signup" data-testid="create-account-link"
                    className="mt-2.5 inline-block rounded-lg bg-shipany-blue px-5 py-2 text-xs font-bold text-white transition-colors hover:bg-[#0648B3]">Create Account</Link>
                </div>
              )}

              {isLogin && (
                <>
                  <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                    <span className="h-px flex-1 bg-slate-200" /> or with mobile OTP <span className="h-px flex-1 bg-slate-200" />
                  </div>
                  <div className="space-y-3" data-testid="otp-login-block">
                    <div className="flex gap-2">
                      <Field icon={Smartphone} testId="otp-phone-input" placeholder="10-digit mobile number" inputMode="numeric" maxLength={10}
                        value={otpForm.phone} onChange={(e) => setOtpForm({ ...otpForm, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
                      <button type="button" onClick={() => { setOtpForm({ ...otpForm, code: "" }); sendOtp(); }} disabled={otpBusy} data-testid="otp-send-btn"
                        className="shrink-0 rounded-xl border border-shipany-blue px-4 text-xs font-bold text-shipany-blue transition-colors hover:bg-shipany-blue/5 disabled:opacity-50">
                        {otpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : otpStage === "sent" ? "Resend" : "Send OTP"}
                      </button>
                    </div>
                    {otpStage === "sent" && (
                      <div className="flex gap-2">
                        <Field icon={KeyRound} testId="otp-code-input" placeholder="6-digit OTP" inputMode="numeric" maxLength={6}
                          value={otpForm.code} onChange={(e) => setOtpForm({ ...otpForm, code: e.target.value.replace(/\D/g, "").slice(0, 6) })} />
                        <button type="button" onClick={verifyOtp} disabled={otpBusy} data-testid="otp-verify-btn"
                          className="shrink-0 rounded-xl bg-[#10B981] px-4 text-xs font-bold text-white transition-colors hover:bg-[#0da271] disabled:opacity-50">
                          {otpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Sign In"}
                        </button>
                      </div>
                    )}
                    <div id="recaptcha-container" data-testid="recaptcha-container" />
                  </div>
                </>
              )}

              <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                <span className="h-px flex-1 bg-slate-200" /> or with email <span className="h-px flex-1 bg-slate-200" />
              </div>

              <form onSubmit={submit} className="space-y-3.5">
                {!isLogin && (
                  <>
                    <Field icon={User} testId="register-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                    <div className="grid grid-cols-2 gap-3">
                      <Field icon={Phone} testId="register-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                      <Field icon={Building2} testId="register-company-input" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                    </div>
                  </>
                )}
                <Field icon={Mail} testId="auth-email-input" type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                <Field icon={Lock} testId="auth-password-input" type="password" placeholder="Password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                {isLogin && (
                  <p className="text-right">
                    <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-shipany-blue hover:underline">Forgot password?</Link>
                  </p>
                )}
                {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600" data-testid="auth-error">{error}</p>}
                <button data-testid="auth-submit-btn" disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] disabled:opacity-60 glow-blue">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isLogin ? "Sign In" : "Create Account"}
                </button>
              </form>

              <p className="mt-5 text-center text-sm text-slate-500">
                {isLogin ? <>New to SHIPANY? <Link to="/signup" data-testid="goto-signup-link" className="font-semibold text-shipany-blue hover:underline">Create account</Link></>
                  : <>Already shipping? <Link to="/login" data-testid="goto-login-link" className="font-semibold text-shipany-blue hover:underline">Sign in</Link></>}
              </p>
            </div>
            <p className="mt-4 text-center text-xs text-slate-400"><Link to="/" className="hover:text-shipany-blue">← Back to shipany.in</Link></p>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}
